import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { getSocket } from '../../socket/socket';

/**
 * UserPresence Component
 * Handles real-time online/offline status for users
 * No UI - just manages presence state via socket events
 */
const UserPresence = () => {
  const { user } = useSelector((state) => state.auth);
  
  useEffect(() => {
    if (!user?.id) return;

    const socket = getSocket();
    if (!socket) {
      console.warn('⚠️ Socket not available for user presence');
      return;
    }

    // Emit user online status when component mounts
    const handleUserOnline = () => {
      console.log(`🟢 User ${user.id} going online`);
      socket.emit('userOnline', { 
        userId: user.id,
        userName: `${user.firstName} ${user.lastName}`,
        timestamp: new Date().toISOString()
      });
    };

    // Emit user offline status when component unmounts or tab closes
    const handleUserOffline = () => {
      console.log(`🔴 User ${user.id} going offline`);
      socket.emit('userOffline', { 
        userId: user.id,
        userName: `${user.firstName} ${user.lastName}`,
        timestamp: new Date().toISOString()
      });
    };

    // Note: Removed visibilitychange handler to prevent socket ID removal
    // when switching tabs or minimizing Chrome in Windows

    // Handle page beforeunload (tab/window closing)
    const handleBeforeUnload = () => {
      console.log(`🚪 User ${user.id} closing tab/window - going offline`);
      handleUserOffline();
    };

    // Emit online status when socket connects
    const handleSocketConnect = () => {
      console.log(`🔌 Socket connected for user ${user.id}`);
      handleUserOnline();
    };

    // Emit offline status when socket disconnects
    const handleSocketDisconnect = () => {
      console.log(`🔌 Socket disconnected for user ${user.id}`);
      // Note: offline will be handled automatically by backend on disconnect
    };

    // Set up event listeners
    if (socket.connected) {
      handleUserOnline();
      // Request current online users when component mounts
      socket.emit('requestPresenceData');
    } else {
      socket.on('connect', handleSocketConnect);
    }

    socket.on('disconnect', handleSocketDisconnect);
    window.addEventListener('beforeunload', handleBeforeUnload);

    // Cleanup function
    return () => {
      console.log(`🧹 Cleaning up UserPresence for user ${user.id}`);
      
      // Remove event listeners
      socket.off('connect', handleSocketConnect);
      socket.off('disconnect', handleSocketDisconnect);
      window.removeEventListener('beforeunload', handleBeforeUnload);
      
      // Note: Removed handleUserOffline() from cleanup to prevent socket ID removal
      // during route changes or component re-renders
    };
  }, [user?.id]);

  // This component renders nothing - it's just for side effects
  return null;
};

export default UserPresence;
